import { useCallback, useEffect, useState } from 'react';
import type { CoreOrganization } from '@/lib/zifekCore';
import {
  fetchTransactionStats,
  listTransactions,
  runTransaction,
  templateMeta,
  TRANSACTION_TEMPLATES,
  txStatusMeta,
  type CoreTransactionRecord,
} from '@/lib/coreTransactions';

interface Props {
  commerceId: number;
  org: CoreOrganization;
}

export default function TransactionsTab({ org }: Props) {
  const [txs, setTxs] = useState<CoreTransactionRecord[]>([]);
  const [stats, setStats] = useState({ total: 0, committed: 0, rolledBack: 0, avgDuration: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [running, setRunning] = useState(false);
  const [selected, setSelected] = useState('create_order');
  const [forceFail, setForceFail] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [list, s] = await Promise.all([listTransactions(org.id), fetchTransactionStats(org.id)]);
      setTxs(list);
      setStats(s);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Chargement impossible.');
    } finally {
      setLoading(false);
    }
  }, [org.id]);

  useEffect(() => { load(); }, [load]);

  const run = async () => {
    setRunning(true);
    setError('');
    setNotice('');
    const tpl = templateMeta(selected);
    try {
      const res = await runTransaction({
        organizationId: org.id,
        tenantId: org.tenant_id,
        name: selected,
        steps: tpl.steps,
        run: async (step, index) => {
          await new Promise((r) => setTimeout(r, 60));
          if (forceFail && index === Math.min(2, tpl.steps.length - 1)) throw new Error(`Étape « ${step} » critique en échec.`);
        },
      });
      setNotice(res.ok ? `Transaction ${res.txId} validée (${res.steps.length} étapes).` : `Transaction annulée & compensée : ${res.error}`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Exécution impossible.');
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="space-y-5">
      <p className="text-xs text-foreground-500 max-w-3xl">
        <span className="font-semibold text-foreground-700">Transactions</span> : les opérations multi-étapes
        (commande + lignes + paiement + réservation de stock) sont atomiques. Si une étape critique échoue,
        on évite un état partiellement créé en compensant les étapes déjà réussies.
      </p>

      {error && <div className="p-3 bg-red-50 border border-red-200 rounded-md text-xs text-red-700">{error}</div>}
      {notice && <div className="p-3 bg-secondary-50 border border-secondary-200 rounded-md text-xs text-secondary-800 flex items-center justify-between"><span>{notice}</span><button onClick={() => setNotice('')} className="cursor-pointer"><i className="ri-close-line"></i></button></div>}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-4">
          <i className="ri-loop-right-line text-lg text-primary-600"></i>
          <p className="text-lg font-bold text-foreground-900 mt-1">{stats.total}</p>
          <p className="text-xs text-foreground-500">Transactions</p>
        </div>
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-4">
          <i className="ri-checkbox-circle-line text-lg text-secondary-600"></i>
          <p className="text-lg font-bold text-foreground-900 mt-1">{stats.committed}</p>
          <p className="text-xs text-foreground-500">Validées</p>
        </div>
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-4">
          <i className="ri-arrow-go-back-line text-lg text-accent-600"></i>
          <p className="text-lg font-bold text-foreground-900 mt-1">{stats.rolledBack}</p>
          <p className="text-xs text-foreground-500">Annulées</p>
        </div>
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-4">
          <i className="ri-timer-line text-lg text-foreground-500"></i>
          <p className="text-lg font-bold text-foreground-900 mt-1">{stats.avgDuration} ms</p>
          <p className="text-xs text-foreground-500">Durée moyenne</p>
        </div>
      </div>

      <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-foreground-900 mb-2 flex items-center gap-2"><i className="ri-flow-chart text-primary-600"></i>Modèles transactionnels</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {TRANSACTION_TEMPLATES.map((t) => (
            <div key={t.key} className="rounded-lg border border-background-200/70 bg-background-100/60 p-3">
              <p className="text-xs font-semibold text-foreground-800 flex items-center gap-2"><i className={`${t.icon} text-primary-600`}></i>{t.label}</p>
              <div className="flex flex-wrap items-center gap-1 mt-2">
                {t.steps.map((s, i) => (
                  <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-background-50 border border-background-200/70 text-foreground-600 flex items-center gap-1">
                    {i > 0 && <i className="ri-arrow-right-s-line text-foreground-400"></i>}{s}
                  </span>
                ))}
              </div>
              <p className="text-[11px] text-foreground-500 mt-1.5">{t.desc}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-foreground-900 mb-2 flex items-center gap-2"><i className="ri-play-line text-accent-600"></i>Tester une transaction</h4>
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
          <select className="w-full sm:max-w-xs px-3 py-2 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-300" value={selected} onChange={(e) => setSelected(e.target.value)}>
            {TRANSACTION_TEMPLATES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
          <label className="flex items-center gap-2 text-xs text-foreground-600 whitespace-nowrap cursor-pointer">
            <input type="checkbox" checked={forceFail} onChange={(e) => setForceFail(e.target.checked)} className="accent-primary-500" />
            Forcer un échec critique
          </label>
          <button onClick={run} disabled={running} className="px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-2 justify-center">
            {running ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-play-line"></i>} Exécuter
          </button>
        </div>
      </div>

      <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-background-200/60 flex items-center gap-2">
          <span className="w-8 h-8 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center"><i className="ri-history-line"></i></span>
          <h4 className="text-sm font-semibold text-foreground-900">Journal des transactions</h4>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-12"><i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i></div>
        ) : txs.length === 0 ? (
          <p className="px-4 py-6 text-xs text-foreground-400 text-center">Aucune transaction.</p>
        ) : (
          <div className="divide-y divide-background-200/60">
            {txs.map((t) => {
              const tm = templateMeta(t.name || '');
              const sm = txStatusMeta(t.status);
              const steps = Array.isArray(t.steps) ? t.steps : [];
              return (
                <div key={t.id} className="px-4 py-3">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="w-9 h-9 rounded-lg bg-background-100 text-foreground-700 flex items-center justify-center flex-shrink-0"><i className={tm.icon}></i></span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-foreground-800 truncate">{tm.label} <span className="text-[11px] text-foreground-400">· {t.tx_id}</span></p>
                      <p className="text-[11px] text-foreground-400">{steps.length} étapes · {t.duration_ms || 0} ms · {t.started_at ? new Date(t.started_at).toLocaleString('fr-FR') : ''}</p>
                    </div>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap ${sm.className}`}>{sm.label}</span>
                  </div>
                  {steps.length > 0 && (
                    <div className="mt-2 pl-12 flex flex-wrap gap-1.5">
                      {steps.map((s, i) => (
                        <span key={i} className={`text-[10px] px-2 py-0.5 rounded-full flex items-center gap-1 ${s.status === 'ok' ? 'bg-secondary-100 text-secondary-900' : s.status === 'failed' ? 'bg-red-100 text-red-800' : 'bg-background-100 text-foreground-600'}`}>
                          <i className={s.status === 'ok' ? 'ri-check-line' : s.status === 'failed' ? 'ri-close-line' : 'ri-time-line'}></i>{s.step}
                        </span>
                      ))}
                    </div>
                  )}
                  {t.error && <p className="mt-1.5 pl-12 text-[11px] text-red-600">{t.error}</p>}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}